import monumentFontCss from "@frontend/design/fonts/fonts.css?inline"
import homeVector from "@frontend/design/icons/home-vector.svg"
import logo from "@frontend/design/icons/logo.svg"
import riIcon from "@frontend/design/icons/ri-icon.svg"
import riLogo from "@frontend/design/icons/ri-logo.svg"
import shieldPositive from "@frontend/design/icons/shield-positive.svg"
import diy from "@frontend/entry-app/assets/images/diy.svg"
import likelyCauses from "@frontend/entry-app/assets/images/likely-causes.svg"
import potentialCauses from "@frontend/entry-app/assets/images/potentials-causes.svg"
import recommend from "@frontend/entry-app/assets/images/recommend.svg"

import {defineCapabilities} from "../base"
import {defaultTheme} from "../default"
import {loadInlineStyle} from "../fontLoader"
import type {
  BrandLinks,
  ThemeAssets,
  ThemeDefinition,
  WidgetChrome,
} from "../types"

const FONT_FAMILY =
  'ABC Monument Grotesk, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

const chrome: WidgetChrome = {
  productName: "Repair Guru",
  productTitle: "Repair Guru",
  headerLogoSrc: logo,
  homeWelcome: null,
  inputInitialPlaceholder: null,
  greetingTitle: "Find the right part faster",
  greetingSubtitleLines: [
    "Tell me the part you need",
    "or the problem you’re having.",
    "I’ll find the exact match.",
  ],
  greetingTitleSparkle: false,
  headerGradient: "linear-gradient(90deg, #015DA0 0%, #009EC8 100%)",
  bubbleFillGradient: "linear-gradient(180deg, #015DA0 0%, #009EC8 100%)",
  bubbleGlowRgba: "rgba(0, 225, 255, 0.45)",
  bubbleGlowPeakRgba: "rgba(0, 225, 255, 0.70)",
  bubbleHoverGlowRgba: "rgba(0, 225, 255, 0.55)",
  conicBorder: `conic-gradient(
      from 90deg,
      #3A8BC4   0deg,
      #00E1FF  66deg,
      #91CCF0 118deg,
      #3A8BC4 178deg,
      #91CCF0 232deg,
      #81DDF7 268deg,
      #70EEFF 307deg,
      #3A8BC4 360deg
    )`,
  starFill: "#00A9D7",
  starGradientStops: [
    {color: "#015DA0"},
    {offset: "0.182692", color: "#00E1FF"},
    {offset: "0.326923", color: "#91CCF0"},
    {offset: "0.483516", color: "#015DA0"},
    {offset: "0.494505", color: "#00E1FF"},
    {offset: "0.644231", color: "#91CCF0"},
    {offset: "0.745192", color: "#81DDF7"},
    {offset: "0.8525", color: "#70EEFF"},
    {offset: "1", color: "#00A9D7"},
  ],
  chipBorder: "#00A9D7",
  cardBorderGradient:
    "linear-gradient(135deg, #015DA0 0%, #00E1FF 18%, #91CCF0 33%, #015DA0 50%, #91CCF0 64%, #81DDF7 75%, #70EEFF 85%, #00A9D7 100%)",
  hexFill: "#00A9D7",
  inputGradient:
    "linear-gradient(90deg, #70EEFF 0%, #00A9D7 25%, #91CCF0 50%, #00E1FF 75%, #015DA0 100%)",
  inputGlowRgba: "rgba(0, 225, 255, 0.40)",
  chatBackground: "#FAFAFA",
  slideoutPanelBg:
    "linear-gradient(180deg, #ffffff 0%, #ffffff 38%, #7eb8d4 72%, #0182ba 100%)",
  slideoutPanelFallback: "#0182ba",
  chatWashGlowA: "rgba(0, 169, 215, 0.07)",
  chatWashGlowB: "rgba(0, 225, 255, 0.05)",
  attentionBorderGradient: `linear-gradient(
      180deg,
      #015da0 0%,
      #00e1ff 18.27%,
      #91ccf0 32.69%,
      #015da0 49.52%,
      #91ccf0 64.42%,
      #81ddf7 74.52%,
      #70eeff 85.25%,
      #00a9d7 100%
    )`,
  attentionBorderGlow: "0 0 16px rgba(0, 225, 255, 0.55)",
  unreadBadgeBorder: "1px solid rgba(1, 93, 160, 0.55)",
  unreadBadgeRing:
    "0 4px 10px rgba(0, 0, 0, 0.18), 0 0 0 2px rgba(0, 169, 215, 0.18)",
  appBackdrop: "#273D5F",
  ink: "#333333",
  surface: "#FFFFFF",
  surfaceMuted: "#F5F5F5",
  borderMuted: "#E5E7EB",
  fontFamily: FONT_FAMILY,
}

const links: BrandLinks = {
  privacyPolicyUrl: "https://www.repairclinic.com/Privacy-Policy",
  allowedLinkUrlPrefixes: [
    "https://www.repairclinic.com",
    "https://www.youtube.com",
    "https://www.rcappliancepartsimages.com",
    "mailto:customerservice@repairclinic.com",
  ],
  supportEmailMailto: "mailto:customerservice@repairclinic.com",
  legalEntityName: "RepairClinic, LLC",
  unsupportedHelpUrls: [
    "https://www.repairclinic.com/Contact-Us",
    "https://www.repairclinic.com/Shipping-Info",
    "https://www.repairclinic.com/Contact-Us",
    "https://www.repairclinic.com/Shop-For-Parts",
  ],
  partnerLogoSrc: null,
  partnerLogoAlt: null,
}

const assets: ThemeAssets = {
  landingLogo: logo,
  compactMark: riLogo,
  accentGlyph: homeVector,
  illustrations: {
    potentialCauses,
    likelyCauses,
    recommend,
    diySteps: diy,
  },
  footerMark: riIcon,
  feedbackMark: riIcon,
  positiveConfirmIcon: shieldPositive,
}

export const riThemeDefinition: ThemeDefinition = {
  id: "ri",
  tokens: defaultTheme,
  chrome,
  links,
  assets,
  fonts: {
    fontFamily: FONT_FAMILY,
    load: () => loadInlineStyle("theme-font-monument", monumentFontCss),
  },
  capabilities: defineCapabilities(),
  remediationCopy: null,
}

export const themeDefinition = riThemeDefinition
